import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../shadcn/ui/Card";
import { ExternalLink } from "lucide-react";
import { Checkbox } from "../shadcn/ui/Checkbox";
import { Label } from "../shadcn/ui/Label";
import { Input } from "../shadcn/ui/Input";

import { RFQformFields as formFields } from "../config/RFQFormFields.config";
import { Button, DynamicField, ThreeLevelCategorySelector } from "@b2b/ui-components";

interface CategoryHierarchy {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  level?: number;
  children?: CategoryHierarchy[];
  productCount?: number;
}

const RFQCreateForm = ({
  switchToDetailed,
  onSubmit,
  onSaveDraft,
  loading = false,
  categories = [],
}: {
  switchToDetailed: () => void;
  onSubmit?: (formData: Record<string, any>) => void;
  onSaveDraft?: (formData: Record<string, any>) => void;
  loading?: boolean;
  categories?: CategoryHierarchy[];
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [filesMap, setFilesMap] = useState<Record<string, File[]>>({});

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      onSaveDraft({ ...formData, files: filesMap });
    } else {
      console.log("Draft Saved:", { ...formData, files: filesMap });
    }
  };

  const handleSubmitRFQ = () => {
    if (onSubmit) {
      onSubmit({ ...formData, files: filesMap });
    } else {
      console.log("Form Submitted:", { ...formData, files: filesMap });
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Quick RFQ Creation
        </h1>
        <p className="text-muted-foreground">
          Create a Request for Quote quickly with essential details. You can
          always switch to detailed mode for more options.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Quick Form</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 🔹 Category Selection (Three-level hierarchy) */}
          {categories.length > 0 && (
            <ThreeLevelCategorySelector
              categories={categories}
              selectedCategoryIds={formData.categoryIds || []}
              onChange={(categoryIds) => handleInputChange("categoryIds", categoryIds)}
              label="Categories"
              required={true}
              allowMultiSelect={true}
            />
          )}

          {/* 🔹 First row (industry, productCategory, subProductCategory) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {formFields.slice(0, 3).map((field) => (
              <DynamicField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={(val) => handleInputChange(field.name, val)}
                filesMap={filesMap}
                setFilesMap={setFilesMap}
              />
            ))}
          </div>

          {/* 🔹 Second row (leadName, quantity) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formFields.slice(3, 5).map((field) => (
              <DynamicField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={(val) => handleInputChange(field.name, val)}
                filesMap={filesMap}
                setFilesMap={setFilesMap}
              />
            ))}
          </div>

          {/* 🔹 Description */}
          <DynamicField
            field={formFields[5]}
            value={formData[formFields[5].name]}
            onChange={(val) => handleInputChange(formFields[5].name, val)}
            filesMap={filesMap}
            setFilesMap={setFilesMap}
          />

          {/* 🔹 File Upload Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {formFields.slice(6).map((field) => (
              <DynamicField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={(val) => handleInputChange(field.name, val)}
                filesMap={filesMap}
                setFilesMap={setFilesMap}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 🔹 Actions */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} disabled={loading}>
            Save Draft
          </Button>
          <Button variant="primary" onClick={handleSubmitRFQ} disabled={loading}>
            {loading ? "Submitting..." : "Submit RFQ"}
          </Button>
        </div>

        <Button
          variant="link"
          className="gap-2 text-primary"
          onClick={switchToDetailed}
        >
          Switch to Detailed Mode
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default RFQCreateForm;
